/**
 * Browser-side storage for in-progress recordings (AIS-9).
 *
 * A recording is written to IndexedDB in timeslice chunks while it is being made, so a crash,
 * refresh or closed tab does not lose it. Only the first chunk of a MediaRecorder session
 * carries the container header, so the saved file is valid only if EVERY chunk is kept, in
 * order, including the first. Nothing here ever drops or filters a chunk.
 *
 * Every function is safe to call when IndexedDB is unavailable (private windows, old
 * browsers, blocked storage): it logs once and resolves with an inert result, and the caller
 * carries on with its in-memory recording exactly as before. Only `assemble` rejects, because
 * a caller that asked for a file needs to know it could not be built.
 *
 * Writes are queued through one chain, so they reach disk in the order they were made even
 * when the browser would otherwise finish them out of order, and `markStopped` can never
 * overtake the last chunk.
 */

const DB_NAME = "ai-scribe-recordings";
const DB_VERSION = 1;
const SESSIONS = "sessions";
const CHUNKS = "chunks";

/** Interval at which the recorder hands over data, and therefore the size of one saved chunk. */
export const RECORDING_TIMESLICE_MS = 10_000;
/** How long a submitted session is kept before it is cleaned up on the next mount. */
export const SUBMITTED_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * How long a recording that was never submitted is kept, however much audio it holds. This is
 * patient consultation audio on a possibly shared computer, so it cannot stay on disk forever.
 * Cleared on the next visit by any signed-in user.
 */
export const UNSUBMITTED_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;
/**
 * A session written to within this window is open in another tab right now (recording, paused,
 * or stopped and waiting to be submitted), so it is not offered as a lost recording. Chunks
 * arrive every RECORDING_TIMESLICE_MS and the owning tab touches the session every
 * HEARTBEAT_INTERVAL_MS, so 30 s tolerates two missed beats.
 */
export const LIVE_SESSION_WINDOW_MS = 30_000;
/** How often the tab that owns a session touches it (see touchSession) while the session is open. */
export const HEARTBEAT_INTERVAL_MS = 10_000;
/** How long createSession may take before the recording carries on without a browser copy. */
export const CREATE_SESSION_TIMEOUT_MS = 2_000;
/** How long opening the database may take before storage is treated as unavailable. */
const OPEN_TIMEOUT_MS = 5_000;

export type RecordingStatus = "recording" | "stopped" | "submitted";

export interface RecordingSession {
  id: string;
  /** Epoch milliseconds. */
  startedAt: number;
  mimeType: string;
  status: RecordingStatus;
  durationMs: number;
  /** The signed-in user who made the recording, so another user on the same browser never sees it. */
  ownerId?: string;
  /** Epoch milliseconds, set by markSubmitted. */
  submittedAt?: number;
  /** Epoch milliseconds of the last chunk written (or the start), so another tab can tell the session is live. */
  lastWriteAt?: number;
  /**
   * Set only on sessions returned by listUnfinished (never stored): the first chunk is missing
   * or the chunks have gaps, so the file may be cut or unplayable.
   */
  incomplete?: boolean;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;
let chain: Promise<unknown> = Promise.resolve();
let warned = false;
let persistRequested = false;

const warnOnce = (error: unknown) => {
  if (warned) return;
  warned = true;
  console.warn("[recordingStore] Browser storage is unavailable; recordings stay in memory only.", error);
};

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise;
  const attempt: Promise<IDBDatabase | null> = new Promise((resolve) => {
    const giveUp = (error: unknown) => {
      warnOnce(error);
      resolve(null);
    };
    try {
      if (typeof indexedDB === "undefined" || !indexedDB) {
        warnOnce("IndexedDB is not available");
        resolve(null);
        return;
      }
      let settled = false;
      const timer = setTimeout(() => {
        settled = true;
        giveUp("Opening browser storage timed out");
      }, OPEN_TIMEOUT_MS);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SESSIONS)) db.createObjectStore(SESSIONS, { keyPath: "id" });
        // Out-of-line keys: [sessionId, seq].
        if (!db.objectStoreNames.contains(CHUNKS)) db.createObjectStore(CHUNKS);
      };
      request.onsuccess = () => {
        const db = request.result;
        if (settled) {
          // Arrived after the timeout gave up on it.
          db.close();
          return;
        }
        clearTimeout(timer);
        const forget = () => {
          if (dbPromise === attempt) dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          forget();
        };
        // The browser dropped the connection (storage cleared, Safari tab suspension).
        db.onclose = forget;
        resolve(db);
      };
      request.onerror = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        giveUp(request.error);
      };
    } catch (error) {
      giveUp(error);
    }
  });
  dbPromise = attempt;
  // A failed or hung open is not cached, so the next operation tries again.
  void attempt.then((db) => {
    if (!db && dbPromise === attempt) dbPromise = null;
  });
  return attempt;
};

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

const chunkRange = (sessionId: string) => IDBKeyRange.bound([sessionId, 0], [sessionId, Infinity]);

/** Runs `op` after every earlier operation has finished. Never rejects: failures resolve to `fallback`. */
const enqueue = <T>(fallback: T, op: (db: IDBDatabase) => Promise<T>): Promise<T> => {
  const next = chain.then(async () => {
    try {
      const db = await openDb();
      if (!db) return fallback;
      return await op(db);
    } catch (error) {
      warnOnce(error);
      // A dead connection throws InvalidStateError on every transaction; reopen next time.
      if ((error as { name?: string } | null)?.name === "InvalidStateError") dbPromise = null;
      return fallback;
    }
  });
  chain = next;
  return next;
};

const newId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Asks the browser not to evict our data under storage pressure. The answer is ignored. Only
 * asked where the browser decides silently: Firefox shows a permission prompt in the middle of
 * a consultation, so it is skipped there, and nothing is asked when it is already granted or
 * refused.
 */
const requestPersistentStorage = async () => {
  if (persistRequested) return;
  persistRequested = true;
  try {
    const storage = navigator.storage;
    if (!storage?.persist) return;
    if (/firefox/i.test(navigator.userAgent ?? "")) return;
    if (await storage.persisted?.()) return;
    let state: string | undefined;
    try {
      state = (await navigator.permissions?.query({ name: "persistent-storage" as PermissionName }))?.state;
    } catch {
      // Safari rejects the unknown permission name, and it is the browser that needs this most
      // (it evicts script-written data after seven days), so ask anyway.
    }
    if (state === "denied") return;
    await storage.persist();
  } catch {
    /* not supported */
  }
};

/** File extension for a recorded mime type ("audio/mp4" gives "mp4", anything unknown gives "webm"). */
export const extensionForMimeType = (mimeType: string): string => {
  const base = mimeType.split(";")[0].trim().toLowerCase();
  if (base === "audio/mp4" || base === "audio/x-m4a" || base === "audio/aac") return "mp4";
  if (base === "audio/ogg") return "ogg";
  return "webm";
};

/**
 * Starts a saved recording. Resolves with its id, or null when browser storage is unavailable,
 * is too slow (see CREATE_SESSION_TIMEOUT_MS) or no owner is given: a recording nobody owns
 * could never be offered back to anyone, so it is never written. Never rejects, and never holds
 * the caller up for longer than the timeout.
 */
export const createSession = (
  options: { mimeType: string; ownerId: string },
  timeoutMs: number = CREATE_SESSION_TIMEOUT_MS,
): Promise<string | null> => {
  if (!options.ownerId) return Promise.resolve(null);
  const created = enqueue<string | null>(null, async (db) => {
    const now = Date.now();
    const session: RecordingSession = {
      id: newId(),
      startedAt: now,
      mimeType: options.mimeType,
      status: "recording",
      durationMs: 0,
      ownerId: options.ownerId,
      lastWriteAt: now,
    };
    const tx = db.transaction(SESSIONS, "readwrite");
    tx.objectStore(SESSIONS).put(session);
    await transactionDone(tx);
    void requestPersistentStorage();
    return session.id;
  });
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      resolve(null);
      // The caller has moved on without it, so remove it when it does arrive.
      void created.then((id) => (id ? deleteSession(id) : undefined));
    }, timeoutMs);
    void created.then((id) => {
      clearTimeout(timer);
      resolve(id);
    });
  });
};

/**
 * Saves one chunk. `seq` is the position in the recording, starting at 0. Resolves true when
 * saved. Also stamps the session's lastWriteAt so other tabs can see it is live. A chunk for a
 * session that no longer exists (deleted elsewhere) is not written, so no orphan chunk is left.
 */
export const appendChunk = (id: string, seq: number, blob: Blob): Promise<boolean> =>
  enqueue(false, async (db) => {
    const tx = db.transaction([SESSIONS, CHUNKS], "readwrite");
    const sessions = tx.objectStore(SESSIONS);
    const session = (await requestResult(sessions.get(id))) as RecordingSession | undefined;
    if (!session) {
      tx.abort();
      return false;
    }
    sessions.put({ ...session, lastWriteAt: Date.now() });
    tx.objectStore(CHUNKS).put(blob, [id, seq]);
    await transactionDone(tx);
    return true;
  });

const updateSession = (id: string, change: (session: RecordingSession) => RecordingSession | null): Promise<void> =>
  enqueue<void>(undefined, async (db) => {
    const tx = db.transaction(SESSIONS, "readwrite");
    const store = tx.objectStore(SESSIONS);
    const session = (await requestResult(store.get(id))) as RecordingSession | undefined;
    const next = session ? change(session) : null;
    if (next) store.put(next);
    await transactionDone(tx);
  });

/**
 * The recorder has stopped. A submitted session is left as it is. Stamps lastWriteAt, so a
 * clip waiting to be submitted in this tab is not offered as "found" in another one.
 */
export const markStopped = (id: string, durationMs: number): Promise<void> =>
  updateSession(id, (s) =>
    s.status === "submitted" ? null : { ...s, status: "stopped", durationMs, lastWriteAt: Date.now() },
  );

/**
 * Heartbeat from the tab that has this session open: stamps lastWriteAt so other tabs can tell it
 * is in use even while nothing is being written (paused, or stopped and waiting to be submitted).
 * A session that was deleted or submitted meanwhile is left alone.
 */
export const touchSession = (id: string): Promise<void> =>
  updateSession(id, (s) => (s.status === "submitted" ? null : { ...s, lastWriteAt: Date.now() }));

export const markSubmitted = (id: string): Promise<void> =>
  updateSession(id, (s) => ({ ...s, status: "submitted", submittedAt: Date.now() }));

const deleteInTransaction = (tx: IDBTransaction, id: string) => {
  tx.objectStore(SESSIONS).delete(id);
  tx.objectStore(CHUNKS).delete(chunkRange(id));
};

/** Removes a session and all of its chunks. Resolves true when they are gone. */
export const deleteSession = (id: string): Promise<boolean> =>
  enqueue<boolean>(false, async (db) => {
    const tx = db.transaction([SESSIONS, CHUNKS], "readwrite");
    deleteInTransaction(tx, id);
    await transactionDone(tx);
    return true;
  });

/**
 * Sessions that were recorded but never submitted, newest first. A session still marked
 * "recording" was cut off mid-recording (crash, refresh, closed tab): its duration is derived
 * from the chunks that reached disk. Sessions with no chunks hold nothing to recover and are
 * left out, and so is any session written to within LIVE_SESSION_WINDOW_MS, which another tab
 * still has open. Only `ownerId`'s sessions are returned; an empty id returns
 * nothing. A session whose chunks do not run 0, 1, 2 ... without gaps is flagged `incomplete`.
 */
export const listUnfinished = (ownerId: string, now: number = Date.now()): Promise<RecordingSession[]> => {
  if (!ownerId) return Promise.resolve([]);
  return enqueue<RecordingSession[]>([], async (db) => {
    const tx = db.transaction([SESSIONS, CHUNKS], "readonly");
    const sessions = (await requestResult(tx.objectStore(SESSIONS).getAll())) as RecordingSession[];
    const candidates = sessions.filter(
      (s) =>
        s.status !== "submitted" &&
        s.ownerId === ownerId &&
        now - (s.lastWriteAt ?? s.startedAt) >= LIVE_SESSION_WINDOW_MS,
    );
    const keys = await Promise.all(
      candidates.map((s) => requestResult(tx.objectStore(CHUNKS).getAllKeys(chunkRange(s.id)))),
    );
    return candidates
      .map((s, i) => ({ session: s, seqs: keys[i].map((key) => (key as [string, number])[1]) }))
      .filter(({ seqs }) => seqs.length > 0)
      .map(({ session, seqs }) => ({
        ...session,
        durationMs: session.status === "recording" ? seqs.length * RECORDING_TIMESLICE_MS : session.durationMs,
        incomplete: seqs.some((seq, index) => seq !== index),
      }))
      .sort((a, b) => b.startedAt - a.startedAt);
  });
};

/**
 * Cleans up on mount, for whichever user is signed in:
 * - submitted sessions older than `submittedMaxAgeMs` (normally deleted straight after submit,
 *   so this catches ones whose delete failed);
 * - unsubmitted sessions older than `unsubmittedMaxAgeMs`, with or without audio;
 * - empty unsubmitted sessions older than `submittedMaxAgeMs`;
 * - chunks whose session no longer exists.
 */
export const purgeStale = (
  submittedMaxAgeMs: number = SUBMITTED_RETENTION_MS,
  now: number = Date.now(),
  unsubmittedMaxAgeMs: number = UNSUBMITTED_RETENTION_MS,
): Promise<void> =>
  enqueue<void>(undefined, async (db) => {
    const tx = db.transaction([SESSIONS, CHUNKS], "readwrite");
    const sessions = (await requestResult(tx.objectStore(SESSIONS).getAll())) as RecordingSession[];
    const submittedCutoff = now - submittedMaxAgeMs;
    const unsubmittedCutoff = now - unsubmittedMaxAgeMs;
    const doomed = new Set<string>();
    const maybeEmpty: RecordingSession[] = [];
    for (const s of sessions) {
      if (s.status === "submitted") {
        if ((s.submittedAt ?? s.startedAt) < submittedCutoff) doomed.add(s.id);
      } else if (s.startedAt < unsubmittedCutoff) {
        doomed.add(s.id);
      } else if (s.startedAt < submittedCutoff) {
        maybeEmpty.push(s);
      }
    }
    const counts = await Promise.all(maybeEmpty.map((s) => requestResult(tx.objectStore(CHUNKS).count(chunkRange(s.id)))));
    maybeEmpty.forEach((s, i) => {
      if (counts[i] === 0) doomed.add(s.id);
    });
    doomed.forEach((id) => deleteInTransaction(tx, id));

    const alive = new Set(sessions.filter((s) => !doomed.has(s.id)).map((s) => s.id));
    const chunkKeys = (await requestResult(tx.objectStore(CHUNKS).getAllKeys())) as [string, number][];
    new Set(chunkKeys.map(([sessionId]) => sessionId)).forEach((sessionId) => {
      if (!alive.has(sessionId) && !doomed.has(sessionId)) tx.objectStore(CHUNKS).delete(chunkRange(sessionId));
    });
    await transactionDone(tx);
  });

/** Joins every saved chunk, in order, into one file. Rejects when nothing was saved. */
export const assemble = (id: string): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    const next = chain.then(async () => {
      const db = await openDb();
      if (!db) throw new Error("Browser storage is unavailable");
      const tx = db.transaction([SESSIONS, CHUNKS], "readonly");
      const [session, chunks] = await Promise.all([
        requestResult(tx.objectStore(SESSIONS).get(id)) as Promise<RecordingSession | undefined>,
        // Keys are [id, seq], so the browser returns the chunks in seq order.
        requestResult(tx.objectStore(CHUNKS).getAll(chunkRange(id))) as Promise<Blob[]>,
      ]);
      if (chunks.length === 0) throw new Error("No saved audio found for this recording");
      return new Blob(chunks, { type: session?.mimeType || chunks[0].type });
    });
    // A failed assemble must not stall later writes.
    chain = next.catch(() => undefined);
    next.then(resolve, reject);
  });
